import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { Store } from '@ngrx/store';
import { ADVANCED_PLANTS_LIMIT, ConnextData, isNotNullOrUndefined, RoleService, System, SystemsService } from 'ngx-core-services';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ToastFacade, UiPromiseService } from 'ngx-zcs-components';

import { CreateBlockReduxSupport, IReduxBlock } from '../../../store/helpers/decorator';
import { selectedSystem, selectedSystemConnextData, SystemDefinition, SystemsState } from '../../../store';
import { Portability } from '../base-systems-porting/base-systems-porting.models';
import { PortingService } from '../porting.service';

const labels = {
  plantIsPortable: "The plant is promotable",
  plantIsPortableButton: "Promote",
  plantPortableDescription: "The plant can be promote to a verified plant. This will allow you to access advanced features.",
  plantPortableAdviceTitle: "You can promote the plant",
  plantPortableAdviceContent: "By promoting the plant, you will be able to access advanced features.",
}

@CreateBlockReduxSupport()
@Component({
  selector: 'lib-system-porting',
  templateUrl: './system-porting.component.html',
  styleUrls: ['./system-porting.component.scss'],
})
export class SystemPortingComponent implements IReduxBlock {
  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  _refresh$ = new Observable<boolean>();
  @Input() refresh$ = new Subject<void>();

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  @Input() onlyAdvice = false;

  get labels(): typeof labels{
    return this._labels;
  }

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly selectedSystem$ = this.store.select(selectedSystem);
  readonly connextData$ = this.store.select(selectedSystemConnextData);
  readonly verifiedSystems$ = this.systemService.awsSystems$;

  readonly checkSystemPortability$ = combineLatest([
    this.selectedSystem$.pipe(
      isNotNullOrUndefined(),
      first(),
    ),
    this.verifiedSystems$.pipe(isNotNullOrUndefined()),
    this.roleService.has_somePermission$.pipe(isNotNullOrUndefined()),
  ]).pipe(
    switchMap(async ([system, verifiedSystems, hasAuthorization]) => {
      if ( system.source === 'aws' ) {
        return { system, portability: Portability.NOT_PORTABLE };
      }
      if (!hasAuthorization && verifiedSystems.length >= ADVANCED_PLANTS_LIMIT) {
        return { system, portability: Portability.NOT_PORTABLE };
      }
      const portability = await this.portingService.determinePortability(system);
      return { system, portability };
    }),
    tap(({ system, portability }) => {
      if (this.onlyAdvice && portability === Portability.PORTABLE && this.portingService.portingSystemUidPopup !== system.uid) {
        this.portingService.portingSystemUidPopup = system.uid;
        this.toastFacade.present(
          labels.plantPortableAdviceContent,
          labels.plantPortableAdviceTitle,
          {
            severity: 'info',
            browserMode: 'persistent',
          },
        );
      }
    }),
    map(({ portability }) => portability),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  readonly Portability = Portability;

  constructor(
    private readonly portingService: PortingService,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    readonly store: Store<SystemsState>,
    private readonly uiPromiseService: UiPromiseService,
    private readonly toastFacade: ToastFacade,
    private readonly systemService: SystemsService,
    private readonly roleService: RoleService,
  ) {
    this.portPlant = this.portPlant.bind(this);
  }

  portPlant(system: SystemDefinition, connextData: Partial<ConnextData> | undefined | null) {
    return () => {
      this.uiPromiseService.run(() => {
        return this.portingService.portPlantToAws(system, connextData ?? {});
      },
      {
        loading: true,
      });
    };
  }
}
